<template>
  <main>
    <SideBar
      :current-language="prismicLang"
      :clc-name="clcName"
      :highlight="sidebarHighlight"
      :dynamic-sidebar="dynamicSidebar"
      @toggle-language="toggleLanguage"
    />
    <section class="CLCNews">
      <a class="CLCNews__back" :href="homeUrl">
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="21" viewBox="0 0 22 21" fill="none" stroke="currentColor" aria-hidden="true">
          <path d="M21 10.5H1" stroke-width="2"/>
          <path d="M10 1.5L1 10.5L10 19.5" stroke-width="2"/>
        </svg>
        <span>{{ t.back }}</span>
      </a>

      <h1 class="CLCNews__title">{{ t.title }}</h1>

      <NewsGrid
        :prismic-lang="prismicLang"
        :clc-segment-value="clcSegmentValue"
      />
    </section>

    <footer>
      <p>
        <img src="../assets/images/rebrand-2026/logo-footer.png" alt="ATT Logo" />
        <span>Corporate Responsibility</span>
      </p>
      <ul>
        <li>
          <a href="https://www.att.com/" target="_blank" rel="noopener" alt="ATT">www.att.com</a>
        </li>
        <li>
          <a href="https://sustainability.att.com/" target="_blank" rel="noopener" alt="Sustainability ATT">sustainability.att.com</a>
        </li>
      </ul>
    </footer>
  </main>
</template>

<script>
import SideBar from './SideBar.vue';
import NewsGrid from './subcomponents/NewsGrid.vue';
import Const from '../constants';
import { mapSidebarData, loadSidebarCache, saveSidebarCache, CLC_HOME_PAGE_TYPE } from './helpers/sidebarHelpers';

export default {
  name: 'CLCNewsList',
  components: {
    SideBar,
    NewsGrid
  },
  data() {
    return {
      prismicLang: 'en-us',
      clcName: sessionStorage.getItem('clcName') || '',
      clcSegmentValue: sessionStorage.getItem('clcSegmentValue') || '',
      // Branding del rail (highlight + doodle), global desde clc_new_home_page.
      sidebarHighlight: { template: 'empty' },
      dynamicSidebar: { logoSrc: '', bgImage: '' }
    };
  },
  computed: {
    t() {
      const texts = {
        'en-us': {
          title: 'All News & Updates',
          back: 'Back to CLC Home',
          pageTitle: 'News & Updates | Connected Learning Center',
          pageDescription: 'All news and updates from your Connected Learning Center.'
        },
        'es-es': {
          title: 'Todas las noticias',
          back: 'Volver al inicio',
          pageTitle: 'Noticias y Actualizaciones | Connected Learning Center',
          pageDescription: 'Todas las noticias y novedades de tu Centro de Aprendizaje Conectado.'
        }
      };
      return texts[this.prismicLang] || texts['en-us'];
    },
    homeUrl() {
      const lang = this.prismicLang === 'en-us' ? 'en' : 'es';
      if (this.clcSegmentValue) {
        return `/${lang}/?center=${this.clcSegmentValue}`;
      }
      return `/${lang}/`;
    }
  },
  methods: {
    setLanguage() {
      if (this.$route.path.startsWith('/es')) {
        this.prismicLang = 'es-es';
      } else if (this.$route.path.startsWith('/en')) {
        this.prismicLang = 'en-us';
      }
    },
    getCenterParam() {
      const urlParams = new URLSearchParams(window.location.search);
      const center = urlParams.get(Const.CENTER);
      if (center) {
        this.clcSegmentValue = center;
      }
    },
    updateMeta() {
      const meta = document.querySelector('meta[name="description"]');
      if (meta) {
        meta.setAttribute('content', this.t.pageDescription);
      }
      document.title = this.t.pageTitle;
    },
    toggleLanguage() {
      const currentQuery = this.$route.query;
      const currentPath = this.$route.path;
      let newPath = currentPath;

      if (this.prismicLang === 'en-us') {
        newPath = currentPath.replace(/^\/en/, '/es');
        this.prismicLang = 'es-es';
      } else {
        newPath = currentPath.replace(/^\/es/, '/en');
        this.prismicLang = 'en-us';
      }

      if (newPath !== currentPath) {
        this.$router.push({ path: newPath, query: currentQuery });
      }
      this.updateMeta();
    },
    // Hidrata el rail desde el cache (evita el flash del fondo default al navegar desde la home).
    hydrateSidebarFromCache() {
      const cached = loadSidebarCache(this.prismicLang);
      if (cached) {
        this.sidebarHighlight = cached.highlight;
        this.dynamicSidebar = cached.dynamicSidebar;
      }
    },
    // Branding global del rail (highlight + doodle) desde el singleton clc_new_home_page.
    async getSidebarBranding() {
      try {
        const response = await this.$prismic.client.getSingle(CLC_HOME_PAGE_TYPE, { lang: this.prismicLang });
        const { highlight, dynamicSidebar } = mapSidebarData(response?.data);
        this.sidebarHighlight = highlight;
        this.dynamicSidebar = dynamicSidebar;
        saveSidebarCache(this.prismicLang, { highlight, dynamicSidebar });
      } catch (error) {
        console.error('METHOD: getSidebarBranding ==> ', error);
      }
    },
  },
  created() {
    this.setLanguage();
    this.hydrateSidebarFromCache();
    this.getCenterParam();
    this.updateMeta();
    this.getSidebarBranding();
  },
  watch: {
    // Recargar el branding del rail al cambiar de idioma (highlight es texto localizado).
    prismicLang() {
      this.getSidebarBranding();
    }
  }
};
</script>
